import { type SkillFlowTouchPoint } from './use-custom-skills-analytics';

/** How far the flow has progressed, named after the event that got it there. */
export type SkillCreateFlowStage = 'viewed' | 'started' | 'draftCardViewed';

export type SkillCreateFlowSessionState = {
	/** Joins every event of a single pass through the flow. */
	traceId: string;
	touchPoint: SkillFlowTouchPoint;
	stage: SkillCreateFlowStage;
};

/**
 * The touch points a start can have come from, keyed by the touch point being started: viewing one
 * of them and then starting from the key is one pass through the flow rather than two, so it keeps
 * its `traceId`. Add an entry whenever one surface leads the user to another.
 *
 * Only a viewed touch point hands off — a flow already started there is a pass of its own.
 */
const VIEWED_ORIGINS_BY_STARTED_TOUCH_POINT: Partial<
	Record<SkillFlowTouchPoint, readonly SkillFlowTouchPoint[]>
> = {
	// The import-skill button is a dropdown offering both of these side by side.
	chatCreateSkillWithChat: ['chatImportSkillButton'],
	chatCreateSkillWithEditor: ['chatImportSkillButton'],
};

let state: SkillCreateFlowSessionState | undefined;

const startNewSession = (
	touchPoint: SkillFlowTouchPoint,
	stage: SkillCreateFlowStage,
): SkillCreateFlowSessionState => {
	state = { traceId: crypto.randomUUID(), touchPoint, stage };

	return state;
};

const continuesSession = (
	session: SkillCreateFlowSessionState | undefined,
	touchPoint: SkillFlowTouchPoint,
): session is SkillCreateFlowSessionState => {
	if (session === undefined) {
		return false;
	}

	if (session.touchPoint === touchPoint) {
		return true;
	}

	return (
		session.stage === 'viewed' &&
		(VIEWED_ORIGINS_BY_STARTED_TOUCH_POINT[touchPoint] ?? []).includes(session.touchPoint)
	);
};

/**
 * The flow spans component trees: the touch point is known where it is viewed or started, but has
 * to reach events fired later by unrelated components, such as the skill draft card. Module scope
 * keeps it available to every `useCustomSkillsAnalytics` consumer without a provider.
 */
export const skillCreateFlowSession = {
	/** Replaces any session in progress, so every call begins a new pass with a new `traceId`. */
	start: (
		touchPoint: SkillFlowTouchPoint,
		stage: SkillCreateFlowStage,
	): SkillCreateFlowSessionState => startNewSession(touchPoint, stage),
	/**
	 * Keeps the session, and so its `traceId`, when this touch point continues the pass through the
	 * flow it is already on: the same touch point again, or one the session's touch point hands off
	 * to. Starts a new session otherwise, no session included. The touch point is updated either way,
	 * so the session reflects where the user actually went.
	 */
	startOrContinue: (
		touchPoint: SkillFlowTouchPoint,
		stage: SkillCreateFlowStage,
	): SkillCreateFlowSessionState => {
		if (!continuesSession(state, touchPoint)) {
			return startNewSession(touchPoint, stage);
		}

		state = { ...state, touchPoint, stage };

		return state;
	},
	/** No-op without a session: a stage on its own says nothing about what led there. */
	advanceTo: (stage: SkillCreateFlowStage): void => {
		if (state) {
			state = { ...state, stage };
		}
	},
	get: (): SkillCreateFlowSessionState | undefined => state,
	clear: (): void => {
		state = undefined;
	},
};
